import { inject, Service } from '@angular/core';
import { catchError, EMPTY, finalize, forkJoin, map, Observable, of, share, tap, timeout } from 'rxjs';
import { Symbol } from '../../models/symbol.model';
import { MARKET_VIEW_TIMEOUT_MS } from './market-data.constants';
import { MarketDataService } from './market-data.service';
import { MarketViewSnapshot, SymbolInfo } from './market-data.model';
import { toSymbol } from './market-data.utils';
import { readStoredSymbolInfo, writeStoredSymbolInfo } from './symbol-info.storage';

/**
 * Every contract the app shows, from two sources:
 * - `symbolinfo` — names, lot, tick, commissions. Changes rarely, so it is cached in
 *   `localStorage`, served from there at once and refreshed once per session in the background.
 * - `market-view` — the session's figures. Always fetched fresh; when it fails or hangs the
 *   list is still shown from `symbolinfo`, with the figures left to the live feed.
 *
 * Live ticks stay with the pages that render them (`MarketViewService`); this store answers
 * the "what is it now" question each page asks when it opens.
 */
@Service()
export class MarketDataStore {
	private readonly api = inject(MarketDataService);

	private symbolInfo: SymbolInfo[] | null = readStoredSymbolInfo();

	private refreshStarted = false;

	private symbolInfoRequest: Observable<SymbolInfo[]> | null = null;

	/**
	 * Contracts sorted by traded value, descending. With no `isin` only active contracts are
	 * listed; asked by `isin` an inactive one is still answered, so a holding or an old order
	 * on an expired certificate keeps its name. `isin` is matched ignoring case — the
	 * portfolio and order endpoints promise no casing.
	 */
	getSymbols(isin?: string | string[]): Observable<Symbol[]> {
		const wanted = isin === undefined ? null : new Set([isin].flat().map(code => code.toUpperCase()));

		return forkJoin([this.loadSymbolInfo(), this.loadMarketView()]).pipe(
			map(([infos, quotes]) => {
				const quoteByContract = new Map(quotes.map(quote => [quote.contractId, quote]));

				return infos
					.filter(info => (wanted ? wanted.has(info.isin.toUpperCase()) : info.isActive))
					.map(info => toSymbol(info, quoteByContract.get(info.contractId)))
					.sort((a, b) => b.tradesValue - a.tradesValue);
			})
		);
	}

	/** One contract by `isin`; undefined when no contract carries it. */
	getSymbol(isin: string): Observable<Symbol | undefined> {
		if (!isin) {
			return of(undefined);
		}
		return this.getSymbols(isin).pipe(map(symbols => symbols[0]));
	}

	/**
	 * The cached list when there is one — refreshed behind it on the first call of the
	 * session — otherwise the request itself.
	 */
	private loadSymbolInfo(): Observable<SymbolInfo[]> {
		if (!this.symbolInfo) {
			return this.requestSymbolInfo();
		}

		if (!this.refreshStarted) {
			// A failed refresh keeps the cache; the next session tries again.
			this.refreshStarted = true;
			this.requestSymbolInfo()
				.pipe(catchError(() => EMPTY))
				.subscribe();
		}

		return of(this.symbolInfo);
	}

	/** The session's figures, or none when the request fails or takes too long. */
	private loadMarketView(): Observable<MarketViewSnapshot[]> {
		return this.api.getMarketView().pipe(
			timeout(MARKET_VIEW_TIMEOUT_MS),
			catchError(() => of([]))
		);
	}

	/** One request at a time, however many pages ask while it is in flight. */
	private requestSymbolInfo(): Observable<SymbolInfo[]> {
		this.symbolInfoRequest ??= this.api.getSymbolInfo().pipe(
			tap(list => {
				this.symbolInfo = list;
				this.refreshStarted = true;
				writeStoredSymbolInfo(list, new Date());
			}),
			finalize(() => (this.symbolInfoRequest = null)),
			share()
		);

		return this.symbolInfoRequest;
	}
}
